import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { User, Image as ImageIcon, Coins, Ruler, Users, Gift, LogOut } from 'lucide-react';
import { formatCredits } from '@/lib/utils';
import { AppView } from '@/types';
import type { SettingsTab } from '@/components/views/SettingsView';

interface AccountMenuProps {
  email: string;
  avatarUrl: string | null;
  credits: number;
  floorplanUnits: number;
  isLoadingCredits: boolean;
  disabledFeatures: string[];
  isAdmin: boolean;
  hasTeam: boolean;
  plan: 'starter' | 'pro' | 'agency' | null;
  subscriptionStatus: string | null;
  onNavigate: (view: AppView) => void;
  onNavigateSettings: (tab: SettingsTab) => void;
  onSignOut: () => void;
}

export const AccountMenu: React.FC<AccountMenuProps> = ({
  email, avatarUrl, credits, floorplanUnits, isLoadingCredits, disabledFeatures, isAdmin, hasTeam,
  plan, subscriptionStatus, onNavigate, onNavigateSettings, onSignOut,
}) => {
  const initials = email.slice(0, 2).toUpperCase();
  const creditLabel = isLoadingCredits ? '…' : formatCredits(credits);
  const floorplanLabel = isLoadingCredits ? '…' : String(floorplanUnits);
  // Mirror the sidebar's feature-gating so users don't see links they can't use.
  const showTeam = isAdmin || hasTeam;
  const showAffiliate = isAdmin || !disabledFeatures.includes('affiliate');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-10 w-10 overflow-hidden rounded-full border-2 border-border bg-secondary hover:border-primary flex items-center justify-center"
          aria-label="Konto"
        >
          {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : <span className="text-sm font-semibold">{initials}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <div className="flex items-center gap-3 px-4 py-3.5">
          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-secondary flex items-center justify-center">
            {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : <span className="text-sm font-semibold">{initials}</span>}
          </div>
          <p className="min-w-0 truncate text-sm text-muted-foreground">{email}</p>
        </div>

        <div className="px-4 pb-3">
          <DropdownMenuItem asChild className="rounded-md p-0 focus:bg-transparent">
            <Button className="w-full" onClick={() => onNavigate('credits')}>
              {subscriptionStatus === 'active' && plan ? 'Abo verwalten' : 'Abo wählen'}
            </Button>
          </DropdownMenuItem>
        </div>

        <DropdownMenuItem onClick={() => onNavigate('credits')} className="justify-between">
          <span className="flex items-center gap-2.5 text-muted-foreground"><Coins className="h-4 w-4 text-primary" />Bild-Credits</span>
          <span className="font-semibold tabular-nums">{creditLabel}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate('credits')} className="justify-between">
          <span className="flex items-center gap-2.5 text-muted-foreground"><Ruler className="h-4 w-4 text-primary" />Grundriss-Einheiten</span>
          <span className="font-semibold tabular-nums">{floorplanLabel}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onNavigateSettings('profile')}><User className="mr-2.5 h-4 w-4 text-muted-foreground" />Profil</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigateSettings('branding')}><ImageIcon className="mr-2.5 h-4 w-4 text-muted-foreground" />Branding</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate('credits')}><Coins className="mr-2.5 h-4 w-4 text-muted-foreground" />Abo &amp; Guthaben</DropdownMenuItem>
        {showTeam && <DropdownMenuItem onClick={() => onNavigateSettings('team')}><Users className="mr-2.5 h-4 w-4 text-muted-foreground" />Team</DropdownMenuItem>}
        {showAffiliate && <DropdownMenuItem onClick={() => onNavigateSettings('affiliate')}><Gift className="mr-2.5 h-4 w-4 text-muted-foreground" />Partnerkonto</DropdownMenuItem>}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSignOut} className="text-destructive focus:text-destructive"><LogOut className="mr-2.5 h-4 w-4" />Abmelden</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
